import React, { useState, memo } from 'react';
import { ArrowLeft, Star, MoreVertical, Plus, Edit2, Mic, AudioLines, ChevronDown, Lock, FileText, Maximize2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ThemeToggle } from '../components/ThemeToggle';
import { Header } from '../components/Header';
import { WorkflowPipeline } from '../components/discovery/WorkflowPipeline';
import { copy } from '../copy';
export function ProjectPage() {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && inputValue.trim()) {
      e.preventDefault();
      navigate('/chat');
    }
  };
  const threads = copy.caseDetail.threads;
  const files = copy.caseDetail.files;
  return <div className="min-h-screen bg-white dark:bg-[#1a1a1a] text-slate-900 dark:text-[#e5e5e5] font-sans selection:bg-slate-200 dark:selection:bg-slate-700 transition-colors duration-200" data-id="element-488">
      <Header title={copy.caseDetail.backLabel} minimal showBackArrow backTo="/cases" data-id="element-489" />

      <div className="mx-auto max-w-[1400px] px-6 py-6" data-id="element-490">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-491">
          {/* Main Content (Left Column) */}
          <div className="flex-1 space-y-8" data-id="element-492">
            {/* Header */}
            <div className="flex items-start justify-between" data-id="element-493">
              <div data-id="element-494">
                <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-2 tracking-tight" data-id="element-495">
                  {copy.caseDetail.title}
                </h1>
                <p className="text-base text-slate-500 dark:text-[#a3a3a3]" data-id="element-496">
                  {copy.caseDetail.subtitle}
                </p>
              </div>
              <div className="flex items-center gap-2" data-id="element-497">
                <button className="p-2.5 hover:bg-slate-100 dark:hover:bg-white/5 rounded-xl transition-all active:scale-95" data-id="element-498">
                  <MoreVertical size={20} className="text-slate-500 dark:text-[#a3a3a3]" data-id="element-499" />
                </button>
                <button className="p-2.5 hover:bg-slate-100 dark:hover:bg-white/5 rounded-xl transition-all active:scale-95" data-id="element-500">
                  <Star size={20} className="text-slate-500 dark:text-[#a3a3a3]" data-id="element-501" />
                </button>
              </div>
            </div>

            {/* Prominent Composer */}
            <div className="relative group" data-id="element-502">
              <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-5 focus-within:border-blue-500 dark:focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-500/10 dark:focus-within:ring-blue-400/10 transition-all shadow-sm hover:shadow-md dark:shadow-xl" data-id="element-503">
                <textarea value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder={copy.caseDetail.composerPlaceholder} className="w-full bg-transparent text-lg outline-none placeholder:text-slate-400 dark:placeholder:text-[#555] text-slate-900 dark:text-white resize-none min-h-[120px] pb-12 leading-relaxed" data-id="element-504" />

                <div className="absolute bottom-5 left-5 flex items-center gap-2" data-id="element-505">
                  <button className="p-2 hover:bg-slate-200 dark:hover:bg-white/5 rounded-lg text-slate-500 dark:text-[#a3a3a3] transition-all active:scale-95" data-id="element-506">
                    <Plus size={20} data-id="element-507" />
                  </button>
                </div>

                <div className="absolute bottom-5 right-5 flex items-center gap-3" data-id="element-508">
                  <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-white/5 text-sm text-slate-500 dark:text-[#a3a3a3] transition-all font-medium" data-id="element-509">
                    <span data-id="element-510">{copy.caseDetail.model}</span>
                    <span className="text-slate-400 dark:text-[#555]" data-id="element-511">
                      {copy.caseDetail.mode}
                    </span>
                    <ChevronDown size={14} data-id="element-512" />
                  </button>
                  <button className="p-2 hover:bg-slate-200 dark:hover:bg-white/5 rounded-lg text-slate-500 dark:text-[#a3a3a3] transition-all active:scale-95" data-id="element-513">
                    <Mic size={20} data-id="element-514" />
                  </button>
                  <button className="p-2 hover:bg-slate-200 dark:hover:bg-white/5 rounded-lg text-slate-500 dark:text-[#a3a3a3] transition-all active:scale-95" data-id="element-515">
                    <AudioLines size={20} data-id="element-516" />
                  </button>
                </div>
              </div>
            </div>

            {/* Thread List */}
            <div className="space-y-1" data-id="element-517">
              {threads.map((thread, i) => <button key={i} onClick={() => navigate('/chat')} className="w-full text-left p-4 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5 active:scale-[0.99] transition-all border-b border-slate-100 dark:border-[#262626] last:border-0 group" data-id="element-518">
                  <div className="font-medium text-slate-900 dark:text-[#e5e5e5] group-hover:text-slate-900 dark:group-hover:text-white mb-1 transition-colors" data-id="element-519">
                    {thread.title}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-[#737373] font-medium" data-id="element-520">
                    Last message {thread.time}
                  </div>
                </button>)}
            </div>
          </div>

          {/* Sidebar (Right Column) */}
          <div className="w-full lg:w-[400px] space-y-6" data-id="element-521">
            {/* Discovery Workflow Pipeline */}
            <WorkflowPipeline data-id="element-522" />

            {/* Memory Section */}
            <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-5 space-y-4 hover:border-slate-300 dark:hover:border-[#444] transition-all" data-id="element-523">
              <div className="flex items-center justify-between" data-id="element-524">
                <h3 className="font-semibold text-slate-900 dark:text-white" data-id="element-525">
                  {copy.caseDetail.memoryTitle}
                </h3>
                <div className="flex items-center gap-2" data-id="element-526">
                  <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-200 dark:bg-[#2a2a2a] text-[10px] font-semibold text-slate-600 dark:text-[#a3a3a3] uppercase tracking-wide" data-id="element-527">
                    <Lock size={10} data-id="element-528" />
                    {copy.caseDetail.privilegedLabel}
                  </div>
                  <button className="p-1.5 hover:bg-slate-200 dark:hover:bg-white/5 rounded-lg text-slate-500 dark:text-[#a3a3a3] transition-all active:scale-95" data-id="element-529">
                    <Edit2 size={14} data-id="element-530" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-slate-600 dark:text-[#a3a3a3] leading-relaxed" data-id="element-531">
                {copy.caseDetail.memoryBody}
              </p>
              <div className="text-[10px] text-slate-400 dark:text-[#555] font-medium uppercase tracking-wide" data-id="element-532">
                {copy.caseDetail.memoryLastUpdated}
              </div>
            </div>

            {/* Instructions Section */}
            <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-5 space-y-4 hover:border-slate-300 dark:hover:border-[#444] transition-all" data-id="element-533">
              <div className="flex items-center justify-between" data-id="element-534">
                <h3 className="font-semibold text-slate-900 dark:text-white" data-id="element-535">
                  {copy.caseDetail.instructionsTitle}
                </h3>
                <button className="p-1.5 hover:bg-slate-200 dark:hover:bg-white/5 rounded-lg text-slate-500 dark:text-[#a3a3a3] transition-all active:scale-95" data-id="element-536">
                  <Plus size={16} data-id="element-537" />
                </button>
              </div>
              <p className="text-sm text-slate-500 dark:text-[#737373] leading-relaxed" data-id="element-538">
                {copy.caseDetail.instructionsBody}
              </p>
            </div>

            {/* Files Section */}
            <div className="rounded-2xl border border-slate-200 dark:border-[#333] bg-slate-50 dark:bg-[#222] p-5 space-y-4 hover:border-slate-300 dark:hover:border-[#444] transition-all" data-id="element-539">
              <div className="flex items-center justify-between" data-id="element-540">
                <h3 className="font-semibold text-slate-900 dark:text-white" data-id="element-541">
                  {copy.caseDetail.filesTitle}
                </h3>
                <div className="flex items-center gap-1" data-id="element-542">
                  <button onClick={() => navigate('/case/files')} className="p-1.5 hover:bg-slate-200 dark:hover:bg-white/5 rounded-lg text-slate-500 dark:text-[#a3a3a3] transition-all active:scale-95" title="Maximize" data-id="element-543">
                    <Maximize2 size={16} data-id="element-544" />
                  </button>
                  <button className="p-1.5 hover:bg-slate-200 dark:hover:bg-white/5 rounded-lg text-slate-500 dark:text-[#a3a3a3] transition-all active:scale-95" data-id="element-545">
                    <Plus size={16} data-id="element-546" />
                  </button>
                </div>
              </div>

              <div className="space-y-2" data-id="element-547">
                <div className="h-1.5 w-full bg-slate-200 dark:bg-[#333] rounded-full overflow-hidden" data-id="element-548">
                  <div className="h-full w-[4%] bg-blue-500 rounded-full transition-all duration-500" data-id="element-549" />
                </div>
                <div className="flex items-center justify-between text-[10px]" data-id="element-550">
                  <span className="text-slate-500 dark:text-[#a3a3a3] font-medium" data-id="element-551">
                    {copy.caseDetail.capacityLabel}
                  </span>
                  <span className="flex items-center gap-1.5 text-slate-500 dark:text-[#a3a3a3] font-medium" data-id="element-552">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" data-id="element-553" />
                    {copy.caseDetail.indexingLabel}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 pt-2" data-id="element-554">
                {files.map((file, i) => <div key={i} className="p-3 rounded-xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] space-y-2 hover:border-slate-300 dark:hover:border-[#444] hover:shadow-sm transition-all cursor-pointer group" data-id="element-555">
                    <div className="text-xs font-medium text-slate-900 dark:text-[#e5e5e5] line-clamp-2 leading-snug group-hover:text-slate-900 dark:group-hover:text-white transition-colors" data-id="element-556">
                      {file.name}
                    </div>
                    <div className="text-[10px] text-slate-500 dark:text-[#555] font-medium" data-id="element-557">
                      {file.lines}
                    </div>
                    <div className="inline-block px-1.5 py-0.5 rounded bg-slate-100 dark:bg-[#2a2a2a] text-[9px] text-slate-500 dark:text-[#737373] font-bold uppercase tracking-wide" data-id="element-558">
                      TEXT
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}